import { useCallback, useMemo } from 'react';
import type { ActionMeta, GroupBase, MultiValue, OptionProps } from 'react-select';
import Select, { components as ReactSelectComponents } from 'react-select';
import { useTheme } from '@/components/Theme';
import type { DataSourceFilter } from '@/types/chat.filters';
import * as Presentation from './Presentation';

type Props = {
  readOnly?: boolean;
  options: DataSourceFilter.Item[];
  value: DataSourceFilter.Item[];
  onChange: (value: DataSourceFilter.Item[], meta: ActionMeta<DataSourceFilter.Item>) => unknown;
};

export function DataSource({ options, readOnly, value, onChange }: Props) {
  const theme = useTheme();

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const selected = useMemo(() => value, [value]);

  const handleChange = useCallback((value: MultiValue<DataSourceFilter.Item>, meta: ActionMeta<DataSourceFilter.Item>) => {
    onChange(Array.from(value), meta);
  }, [onChange]);

  const handleNoOptionsMessage = useCallback((obj: { inputValue: string }) => {
    return obj.inputValue?.length ? `No source found` : null;
  }, []);

  return (
    <Presentation.Row>
      <Presentation.Label>Data Source</Presentation.Label>
      <Presentation.Value>
        <Select<DataSourceFilter.Item, true>
          styles={selectStyles}
          components={{
            Option: MenuOption,
          }}
          isDisabled={readOnly}
          theme={selectTheme}
          value={selected}
          isMulti
          options={options}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isSearchable={false}
          isClearable={false}
          placeholder={`Select data sources...`}
          noOptionsMessage={handleNoOptionsMessage}
          getOptionLabel={o => o.name}
          getOptionValue={o => o.id}
          onChange={handleChange}
          menuPlacement="auto" />
      </Presentation.Value>
    </Presentation.Row>
  );
}

function MenuOption(props: OptionProps<DataSourceFilter.Item, true, GroupBase<DataSourceFilter.Item>>) {
  return (
    <ReactSelectComponents.Option {...props}>
      <input
        name={props.data.id}
        type="checkbox"
        checked={props.isSelected} />
      {props.label}
    </ReactSelectComponents.Option>
  );
}